/**
 * Users Settings Page
 *
 * Manage users in the company.
 */

import { useEffect, useState } from 'react';
import { cn, formatDate } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { AppLayout, PageHeader } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge, RoleBadge } from '@/components/ui/Badge';
import { Modal, ConfirmModal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { useIsAdmin, useUser, useCompany } from '@/stores/authStore';
import { supabase } from '@/lib/supabase';
import type { User, UserRole } from '@/types';
import { Plus, Trash2, Mail, Shield, UserCircle2 } from 'lucide-react';

export function UsersSettingsPage() {
  const currentUser = useUser();
  const company = useCompany();
  const isAdmin = useIsAdmin();
  const { success: showSuccess, error: showError } = useToast();

  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    name: '',
    role: 'member' as UserRole,
  });

  useEffect(() => {
    if (!company?.id) return;

    Logger.info(LogCategory.UI, 'UsersSettingsPage mounted', 'UsersSettingsPage');

    async function fetchUsers() {
      try {
        const { data: dbUsers, error } = await supabase
          .from('users')
          .select('*')
          .eq('company_id', company!.id)
          .order('created_at');

        if (error) throw error;

        const mapped: User[] = (dbUsers || []).map((u: any) => ({
          id: u.id,
          email: u.email,
          name: u.name,
          role: u.role,
          companyId: u.company_id,
          avatarUrl: u.avatar_url,
          createdAt: u.created_at,
          lastLoginAt: u.last_login_at,
          isActive: u.is_active,
          authProvider: u.auth_provider,
          isSuperAdmin: u.is_super_admin,
          emailVerified: u.email_verified,
        }));

        setUsers(mapped);
      } catch (err) {
        Logger.error(LogCategory.DATA, 'Failed to fetch users', 'UsersSettingsPage', undefined, err);
        showError('Error', 'Failed to load team members.');
      } finally {
        setIsLoading(false);
      }
    }

    fetchUsers();
  }, [company?.id]);

  const handleAddUser = async () => {
    Logger.info(LogCategory.DATA, 'Adding new user', 'UsersSettingsPage', {
      email: newUserData.email,
      role: newUserData.role,
    });

    if (!newUserData.email || !newUserData.name) {
      showError('Missing information', 'Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if user already exists in this company
      const existing = users.find(u => u.email.toLowerCase() === newUserData.email.toLowerCase());
      if (existing) {
        showError('Already exists', 'A user with this email is already on the team.');
        setIsSubmitting(false);
        return;
      }

      // Create user via Edge Function (creates auth user + profile)
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            email: newUserData.email.toLowerCase(),
            name: newUserData.name,
            role: newUserData.role,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to add user');

      // Refresh user list
      const { data: dbUsers } = await supabase
        .from('users')
        .select('*')
        .eq('company_id', company!.id)
        .order('created_at');

      const mapped: User[] = (dbUsers || []).map((u: any) => ({
        id: u.id,
        email: u.email,
        name: u.name,
        role: u.role,
        companyId: u.company_id,
        avatarUrl: u.avatar_url,
        createdAt: u.created_at,
        lastLoginAt: u.last_login_at,
        isActive: u.is_active,
        authProvider: u.auth_provider,
        isSuperAdmin: u.is_super_admin,
        emailVerified: u.email_verified,
      }));
      setUsers(mapped);

      setShowAddModal(false);
      setNewUserData({ email: '', name: '', role: 'member' });
      showSuccess('User added', `${newUserData.name} has been added to the team.`);
    } catch (err: any) {
      Logger.error(LogCategory.DATA, 'Failed to add user', 'UsersSettingsPage', undefined, err);
      showError('Failed to add user', err?.message || 'Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    Logger.info(LogCategory.DATA, 'Deactivating user', 'UsersSettingsPage', {
      userId: userToDelete.id,
    });

    try {
      const { error } = await (supabase
        .from('users') as any)
        .update({ is_active: false })
        .eq('id', userToDelete.id);

      if (error) throw error;

      setUsers((prev) =>
        prev.map((u) =>
          u.id === userToDelete.id ? { ...u, isActive: false } : u
        )
      );
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      showSuccess('User deactivated', `${userToDelete.name} has been deactivated.`);
    } catch (err) {
      Logger.error(LogCategory.DATA, 'Failed to deactivate user', 'UsersSettingsPage', undefined, err);
      showError('Error', 'Failed to deactivate user.');
    }
  };

  const openDeleteConfirm = (user: User) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  return (
    <AppLayout>
      <PageHeader
        title="User Management"
        description="Manage team members and their access levels"
        actions={
          isAdmin && (
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setShowAddModal(true)}
            >
              Add User
            </Button>
          )
        }
      />

      <div className="max-w-4xl">
        <Card>
          <CardContent padding="none">
            {isLoading ? (
              <div className="p-6">
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4 animate-pulse">
                      <div className="h-12 w-12 bg-slate-200 rounded-full" />
                      <div className="flex-1">
                        <div className="h-4 bg-slate-200 rounded w-32 mb-2" />
                        <div className="h-3 bg-slate-200 rounded w-48" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center">
                <UserCircle2 className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                <p className="text-slate-600">No team members found.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className={cn(
                      'flex items-center justify-between p-4',
                      !user.isActive && 'opacity-50'
                    )}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-medium text-lg">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{user.name}</span>
                          {user.id === currentUser?.id && (
                            <Badge variant="info" size="sm">You</Badge>
                          )}
                          {!user.isActive && (
                            <Badge variant="default" size="sm">Inactive</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{user.email}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <RoleBadge role={user.role} />
                          <span className="text-xs text-slate-400">
                            Joined {formatDate(user.createdAt, 'short')}
                          </span>
                          {user.authProvider !== 'local' && (
                            <span className="text-xs text-slate-400">
                              via {user.authProvider === 'google' ? 'Google' : 'SSO'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {isAdmin && user.id !== currentUser?.id && user.isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteConfirm(user)}
                        className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Role descriptions */}
        <div className="mt-6 p-4 bg-slate-50 rounded-lg">
          <h3 className="text-sm font-medium text-slate-900 mb-3">Role Permissions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Shield className="h-4 w-4 text-danger-500" />
                <span className="font-medium text-slate-700">Admin</span>
              </div>
              <p className="text-slate-500">Full access to all features including user management and billing</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <UserCircle2 className="h-4 w-4 text-primary-500" />
                <span className="font-medium text-slate-700">Member</span>
              </div>
              <p className="text-slate-500">Can view and interact with phone data, access settings</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <UserCircle2 className="h-4 w-4 text-slate-500" />
                <span className="font-medium text-slate-700">Viewer</span>
              </div>
              <p className="text-slate-500">Read-only access to phone data</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setNewUserData({ email: '', name: '', role: 'member' });
        }}
        title="Add Team Member"
        description="Add a new user to your team"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddUser} isLoading={isSubmitting}>
              Add User
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Full Name"
            placeholder="Jane Smith"
            value={newUserData.name}
            onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
            required
          />
          <Input
            label="Email Address"
            type="email"
            placeholder="jane@company.com"
            value={newUserData.email}
            onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
            leftIcon={<Mail className="h-4 w-4" />}
            required
          />
          <Select
            label="Role"
            value={newUserData.role}
            onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value as UserRole })}
            options={[
              { value: 'admin', label: 'Admin - Full access' },
              { value: 'member', label: 'Member - Standard access' },
              { value: 'viewer', label: 'Viewer - Read-only' },
            ]}
          />
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteUser}
        title="Deactivate User"
        message={`Are you sure you want to deactivate ${userToDelete?.name}? They will lose access immediately.`}
        confirmLabel="Deactivate"
        variant="danger"
      />
    </AppLayout>
  );
}
